/**
 * @name: DataLayer Track
 * @description: GTM dataLayer events from Liquid markers and product JSON.
 * @dependencies: window._theme, [data-product-json], #variants_data
 * @strict-mode: true
 * @note: Outlet — out_of_stock_view + out_of_stock_form_submit only (same structure as Market).
 */
'use strict';

(() => {
  const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  const EVENT_MARKER = '[data-datalayer-event]';
  const SKIP_CLICK_EVENTS = new Set(['out_of_stock_view']);

  class DatalayerTrack {
    constructor() {
      this.viewedOutOfStockIds = new Set();
      this.pdpProductData = null;
      this.pdpVariants = null;
    }

    init() {
      this.loadPdpJson();
      document.addEventListener('click', this.onClick.bind(this), true);
      document.addEventListener('submit', this.onSubmit.bind(this), true);
      this.bindOutOfStockView();
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      const marker = target?.closest?.(EVENT_MARKER);
      if (!marker) return;

      const parsed = this.parseMarker(marker);
      if (!parsed.event || SKIP_CLICK_EVENTS.has(parsed.event)) return;
      if (marker.matches('[type="submit"]')) return; // handled in onSubmit

      const fields = this.buildFields(parsed.event, marker, target);
      if (fields) this.pushEvent(parsed.event, fields);
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const submitter = event.submitter instanceof Element ? event.submitter : form;
      const marker = submitter.closest(EVENT_MARKER) || form.querySelector(EVENT_MARKER);
      if (!marker) return;

      const parsed = this.parseMarker(marker);
      if (!parsed.event) return;

      const fields = this.buildFields(parsed.event, marker, form);
      if (fields) this.pushEvent(parsed.event, fields);
    }

    pushEvent(eventName, fields = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: eventName,
        ...this.getPageContext(),
        ...fields,
      });
    }

    bindOutOfStockView() {
      const form = this.findMarker('out_of_stock_view');
      if (!form || form.hidden || form.classList.contains('hidden')) return;
      if (form.closest('[data-quickview-modal]')) return;

      const product = this.findProduct(form) || this.pdpProductData;
      if (!this.hasProduct(product) || product.available === true) return;

      const item = this.buildItem(product, this.findVariant(form));
      item.stock_status = 'out_of_stock';
      if (this.viewedOutOfStockIds.has(item.product_id)) return;

      this.viewedOutOfStockIds.add(item.product_id);
      this.pushEvent('out_of_stock_view', { stock_status: 'out_of_stock', items: [item] });
    }

    buildFields(eventName, marker, target) {
      if (eventName === 'out_of_stock_form_submit') {
        return this.outOfStockSubmitFields(marker, target);
      }
      // Future events plug in here (same as Market)
      return null;
    }

    outOfStockSubmitFields(marker, target) {
      const root = marker.closest('[data-notify-me-modal], [data-backinstockform], notify-me') || marker;
      const email = root.querySelector('input[type="email"], input[name="txtEmailAddress"]');
      if (!email || !EMAIL_REGEX.test(this.normalizeText(email.value))) return null;

      const product = this.findProduct(root) || this.pdpProductData;
      if (!this.hasProduct(product)) return null;

      const item = this.buildItem(product, this.findVariant(marker));
      item.stock_status = 'out_of_stock';

      return {
        click_text: this.normalizeText(marker.innerText) || 'NOTIFY ME',
        stock_status: 'out_of_stock',
        items: [item],
      };
    }

    findProduct(el) {
      if (!el) return this.pdpProductData;

      // notify-me moves to <body> — look up card by data-id
      const notifyHost = el.closest('notify-me');
      if (notifyHost?.dataset?.id) {
        const card = document.querySelector(`[data-product-id="product-${notifyHost.dataset.id}"]`);
        const json = this.readProductJson(card);
        if (json) return json;
      }

      const card = el.closest('[data-product-id]');
      if (card) {
        const json = this.readProductJson(card);
        if (json) return json;
      }

      return this.pdpProductData;
    }

    readProductJson(root) {
      if (!root) return null;
      const script = root.querySelector('[data-product-json]');
      const parsed = this.parseJson(script && (script.textContent || script.getAttribute('data-product-json')));
      return this.hasProduct(parsed) ? parsed : null;
    }

    findVariant(marker) {
      const product = this.findProduct(marker) || this.pdpProductData;
      const variants =
        (this.pdpVariants && this.pdpVariants.length && this.pdpVariants) ||
        product?.variants ||
        [];

      const idInput =
        document.querySelector('form[data-type="add-to-cart-form"] [name="id"]') ||
        marker?.closest('form')?.querySelector('[name="id"]');
      const variantId =
        idInput?.value ||
        new URLSearchParams(window.location.search).get('variant') ||
        product?.variant_id ||
        '';

      if (variantId && variants.length) {
        return variants.find((v) => String(v.id) === String(variantId)) || variants[0];
      }

      if (product?.variant_id) {
        return {
          id: product.variant_id,
          price: product.price,
          compare_at_price: product.compare_at_price,
        };
      }

      return variants[0] || null;
    }

    buildItem(product, variant) {
      const productId = product.id || product.product_id || '';
      const variantId = variant?.id || variant?.variant_id || product.variant_id || '';
      const rawPrice = variant?.price ?? product.price ?? product.final_price ?? 0;
      const rawCompare = variant?.compare_at_price ?? product.compare_at_price ?? product.compareAtPrice ?? 0;
      const price = this.parseShopifyPrice(rawPrice);
      const compareAt = this.parseShopifyPrice(rawCompare);
      const discount =
        compareAt > price
          ? Number((compareAt - price).toFixed(2))
          : this.parseShopifyPrice(product.discount || product.total_discount || 0);

      return {
        item_id: `shopify_${productId}_${variantId}`,
        product_id: String(productId),
        variant_id: String(variantId),
        item_name: product.title || product.product_title || '',
        quantity: 1,
        price,
        discount,
        item_brand: product.vendor || '',
        stock_status: 'out_of_stock',
      };
    }

    parseMarker(el) {
      const raw = this.normalizeText(el?.getAttribute('data-datalayer-event'));
      const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
      return { event: parts[0] || '', sectionName: parts.slice(1).join(', ') };
    }

    findMarker(eventName) {
      for (const node of document.querySelectorAll(EVENT_MARKER)) {
        if (this.parseMarker(node).event === eventName) return node;
      }
      return null;
    }

    loadPdpJson() {
      const script = document.querySelector('.product [data-product-json]');
      this.pdpProductData = this.parseJson(script && (script.textContent || script.getAttribute('data-product-json')));
      const variantsNode = document.getElementById('variants_data');
      this.pdpVariants = this.parseJson(variantsNode && variantsNode.textContent);
    }

    parseJson(text) {
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    }

    hasProduct(product) {
      return Boolean(product && (product.id || product.product_id || product.title || product.product_title));
    }

    getPageContext() {
      return {
        page_path: window.location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      };
    }

    parseShopifyPrice(val) {
      if (val == null) return 0;

      const n = Number(val);
      if (isNaN(n)) return 0;

      if (Number.isInteger(n) && n > 0 && String(val).indexOf('.') === -1) {
        return +(n / 100).toFixed(2);
      }

      return +n.toFixed(2);
    }

    normalizeText(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }
  }

  window._theme = window._theme || {};
  window._theme.datalayerTrack = new DatalayerTrack();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window._theme.datalayerTrack.init());
  } else {
    window._theme.datalayerTrack.init();
  }
})();
